"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { Link } from "react-router-dom"

const CreateProduct = () => {
  const [productInfo, setProductInfo] = useState({
    code: "",
    name: "",
    category: "",
    material: "",
    description: "",
  })
  const [isProductInfoOpen, setIsProductInfoOpen] = useState(true)
  const [wordCount, setWordCount] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [categories, setCategories] = useState([])
  const [materials, setMaterials] = useState([])

  const [filteredCategories, setFilteredCategories] = useState([])
  const [filteredMaterials, setFilteredMaterials] = useState([])
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false)

  useEffect(() => {
    fetchCategories()
    fetchMaterials()
  }, [])

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `https://logicx.premierlam.com/api/pixel-get-product-categories`,
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      )
      if (response.data?.data) {
        setCategories(response.data.data)
        setFilteredCategories(response.data.data)
      }
    } catch (err) {
      console.error("Error fetching categories:", err)
    }
  }

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(
        `https://logicx.premierlam.com/api/pixel-get-product-material`,
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      )
      if (response.data?.data) {
        setMaterials(response.data.data)
        setFilteredMaterials(response.data.data)
      }
    } catch (err) {
      console.error("Error fetching materials:", err)
    }
  }

const handleInputChange = (e) => {
  const { name, value } = e.target;

  // Description with 10 word limit
  if (name === "description") {
  setProductInfo((prev) => ({ ...prev, description: value }));
  setWordCount(value.trim() ? value.trim().split(/\s+/).length : 0); // optional
  return;
}

  // Other fields
  setProductInfo((prev) => ({ ...prev, [name]: value }));

  if (name === "category") {
    const filtered = categories.filter((c) =>
      c.category_name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCategories(filtered);
    setShowCategoryDropdown(true);
  }

  if (name === "material") {
    const filtered = materials.filter((m) =>
      m.product_material.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredMaterials(filtered);
    setShowMaterialDropdown(true);
  }
};


  const handleCategorySelect = (name) => {
    setProductInfo((prev) => ({ ...prev, category: name }))
    setShowCategoryDropdown(false)
  }

  const handleMaterialSelect = (name) => {
    setProductInfo((prev) => ({ ...prev, material: name }))
    setShowMaterialDropdown(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      setError(null)
      setSuccess(false)

      const response = await axios.post(
        `https://logicx.premierlam.com/api/pixel-add-product`,
        {
          product_code: productInfo.code,
          product_name: productInfo.name,
          category_name: productInfo.category,
          product_material: productInfo.material,
          product_description: productInfo.description,
        },
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      )

      if (response.data) {
        setSuccess(true)
        setProductInfo({
          code: "",
          name: "",
          category: "",
          material: "",
          description: "",
        })
        setWordCount(0)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create product")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-700">Create Product</h1>
            <p className="text-gray-500">Create new product</p>
          </div>
          <Link
            to="/all-products"
            className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Product
          </Link>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600">Product created successfully!</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
            <div
              className="flex justify-between items-center p-6 cursor-pointer"
              onClick={() => setIsProductInfoOpen(!isProductInfoOpen)}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-medium text-gray-700">Product Information</h2>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform ${isProductInfoOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {isProductInfoOpen && (
              <div className="p-6 border-t border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Code *</label>
                    <input
                      type="text"
                      name="code"
                      value={productInfo.code}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
                      required
                    />
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={productInfo.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
                      required
                    />
                  </div>

                  {/* Category - Searchable field */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Category *</label>
                    <input
                      type="text"
                      name="category"
                      value={productInfo.category}
                      onChange={handleInputChange}
                      onFocus={() => setShowCategoryDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
                      placeholder="Search or select category"
                      required
                    />
                    {showCategoryDropdown && (
                      <ul className="absolute z-10 bg-white border border-gray-200 rounded-md shadow-md mt-1 w-full max-h-40 overflow-y-auto">
                        {filteredCategories.length > 0 ? (
                          filteredCategories.map((cat) => (
                            <li
                              key={cat.id}
                              onClick={() => handleCategorySelect(cat.category_name)}
                              className="px-4 py-2 hover:bg-orange-100 cursor-pointer text-sm"
                            >
                              {cat.category_name}
                            </li>
                          ))
                        ) : (
                          <li className="px-4 py-2 text-gray-500 text-sm">No results found</li>
                        )}
                      </ul>
                    )}
                  </div>

                  {/* Material - Searchable field */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Material *</label>
                    <input
                      type="text"
                      name="material"
                      value={productInfo.material}
                      onChange={handleInputChange}
                      onFocus={() => setShowMaterialDropdown(true)}
                      onBlur={() => setTimeout(() => setShowMaterialDropdown(false), 200)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
                      placeholder="Search or select material"
                      required
                    />
                    {showMaterialDropdown && (
                      <ul className="absolute z-10 bg-white border border-gray-200 rounded-md shadow-md mt-1 w-full max-h-40 overflow-y-auto">
                        {filteredMaterials.length > 0 ? (
                          filteredMaterials.map((mat) => (
                            <li
                              key={mat.id}
                              onClick={() => handleMaterialSelect(mat.product_material)}
                              className="px-4 py-2 hover:bg-orange-100 cursor-pointer text-sm"
                            >
                              {mat.product_material}
                            </li>
                          ))
                        ) : (
                          <li className="px-4 py-2 text-gray-500 text-sm">No results found</li>
                        )}
                      </ul>
                    )}
                  </div>

             
                 {/* Description */}
<div className="md:col-span-2">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Product Description
  </label>
  <textarea
    name="description"
    value={productInfo.description}
    onChange={handleInputChange}
    rows={5}
    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 outline-none"
  ></textarea>
  <p className="text-sm text-gray-500 mt-1">
    (words {wordCount})
  </p>
</div>

                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <button type="button" className="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900">
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateProduct

